<template>
  <a-table
    class="attribute-table sku-table-css"
    bordered
    size="small"
    :data-source="itemList"
    :pagination="false"
    rowKey="id"
    :locale="{ emptyText: '暂无属性' }"
  >
    <a-table-column key="name" data-index="name" title="属性名称" width="150px"></a-table-column>
    <a-table-column key="value" data-index="value" title="属性值">
      <template #default="{ text: value }">
        {{ handleAttrValue(value) }}
      </template>
    </a-table-column>
    <a-table-column key="action" title="操作" width="200px">
      <template v-slot="{ record, index }">
        <a-button type="link" @click="setAttrForm(record)">编辑</a-button>
        <a-button v-if="record.isNew || can_add_item == 1" type="link" danger @click="removeAttrItem(record)">移除</a-button>
        <a-checkbox :checked="record.id == mainAttrId" style="margin-left: 10px; white-space: nowrap" @change="(e) => changeMainAttr(e, record)">
          主要属性
        </a-checkbox>
        <a-tooltip v-if="index == 0" overlayClassName="tooltip-box" placement="left">
          <template v-slot:title>
            <div style="width: 300px; margin-bottom: 5px">若选择某一【规格】为“主要属性”，则前台只选择该【规格】时，也可显示规格对应的图</div>
            <img src="@/assets/example/integral_attr_set_tip.png" style="width: 300px" />
          </template>
          <a>示例</a>
        </a-tooltip>
      </template>
    </a-table-column>
    <template v-slot:footer v-if="itemList.length < 3 && (!isEdit || can_add_item == 1)">
      <div class="add-btn" @click="setAttrForm()">
        <ehf-icon icon="add-circle-line" style="line-height: 25px"></ehf-icon>
        <div style="line-height: 25px; margin-left: 5px">添加属性</div>
      </div>
    </template>
  </a-table>

  <!-- 属性新增/编辑 -->
  <a-modal
    v-model:open="attributeModal.show"
    :title="attributeModal.title"
    centered
    width="800px"
    okText="保存"
    @ok="saveAttribute"
    destroyOnClose
    :getContainer="getContainer"
  >
    <a-row style="align-items: center">
      <div>属性名：</div>
      <el-input v-model="attributeModal.name" placeholder="请输入属性名" style="width: 200px" />
    </a-row>
    <a-row style="margin-top: 15px">属性值：</a-row>
    <a-row :gutter="16" class="attr-value-list">
      <a-col class="attr-value" :span="8" v-for="(item, i) in attributeModal.list" :key="item.id">
        <a-input class="attr-value-input" v-model:value="item.name" placeholder="输入属性值" ref="attrInputRef" @pressEnter="addAttrValue()" />
        <ehf-icon
          v-if="attributeModal.list.length > 1 && (!isEdit || (isEdit && (item.isNew || can_add_item == 1)))"
          class="attr-value-btn remove"
          icon="delete-bin-5-line"
          @click="removeAttrValue(i)"
        ></ehf-icon>
        <ehf-icon v-if="attributeModal.list.length - 1 === i" class="attr-value-btn add" icon="add-circle-line" @click="addAttrValue()"></ehf-icon>
      </a-col>
    </a-row>
  </a-modal>
</template>

<script setup>
  import { message } from 'ant-design-vue'
  import { ref, reactive, toRefs, watch, nextTick } from 'vue'
  import { deepClone, isNull } from '../../../utils'

  const props = defineProps({
    getContainer: {
      type: Function,
      default: () => {
        return document.body
      },
    },
    attributeList: {
      type: Array,
      default: () => {
        return []
      },
    },
    attributeId: {
      type: Number,
      default: 1,
    },
    mainAttrId: {
      type: Number,
      default: 0,
    },
    can_add_item: {
      type: [Number, String],
      default: 1,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  })
  const emit = defineEmits(['update:attributeList', 'update:attributeId', 'update:mainAttrId', 'update', 'del'])
  const { getContainer, attributeList, attributeId, mainAttrId } = toRefs(props)
  message.config({
    getContainer: getContainer.value,
  })
  watch(getContainer, (val) => {
    message.config({
      getContainer: val,
    })
  })

  const itemList = ref(attributeList.value)
  const attrId = ref(attributeId.value)
  const mainId = ref(mainAttrId.value)
  const attributeModal = reactive({
    show: false,
    type: 'add',
    title: '新增属性',
    id: 1,
    name: '',
    list: [],
    isNew: false,
  })
  // 列表数据监听
  watch(attributeList, (val) => (itemList.value = val))
  watch(itemList, (val) => emit('update:attributeList', val))
  // 属性id数据监听
  watch(attributeId, (val) => (attrId.value = val))
  watch(attrId, (val) => emit('update:attributeId', val))
  // 注意属性id数据监听
  watch(mainAttrId, (val) => (mainId.value = val))
  watch(mainId, (val) => emit('update:mainAttrId', val))

  // 设置属性
  const setAttrForm = (record) => {
    if (record) {
      const item = deepClone(record)
      attributeModal.title = '编辑属性'
      attributeModal.type = 'edit'
      attributeModal.name = item.name
      attributeModal.list = item.value
      attributeModal.id = item.id
      attributeModal.isNew = item.isNew
    } else {
      attributeModal.title = '新增属性'
      attributeModal.type = 'add'
      attributeModal.name = ''
      attributeModal.list = []
      attributeModal.id = attrId.value
      attributeModal.isNew = true
      attrId.value += 1
      addAttrValue()
    }
    attributeModal.show = true
  }
  // 添加属性值
  const attrInputRef = ref(null)
  const addAttrValue = () => {
    attributeModal.list.push({
      isNew: true,
      id: attrId.value,
      name: '',
    })
    nextTick(() => {
      const index = attributeModal.list.findIndex((v) => v.id == attrId.value)
      attrInputRef.value[index].focus()
      attrId.value += 1
    })
  }
  // 移除属性值
  const removeAttrValue = (i) => {
    attributeModal.list.splice(i, 1)
  }
  // 保存属性
  const saveAttribute = () => {
    if (isNull(attributeModal.name)) {
      return message.error('请输入属性名')
    }
    const emptyValue = attributeModal.list.find((v) => v.name === '')
    if (emptyValue) {
      return message.error('请输入属性值')
    }
    const item = {
      isNew: attributeModal.isNew,
      id: attributeModal.id,
      name: attributeModal.name,
      value: attributeModal.list,
    }
    if (attributeModal.type === 'add') {
      itemList.value.push(item)
      emit('update')
    } else {
      const index = itemList.value.findIndex((v) => v.id === item.id)
      itemList.value.splice(index, 1, item)
      emit('update', true)
    }
    attributeModal.show = false
  }
  // 移除属性
  const removeAttrItem = (record) => {
    const index = itemList.value.findIndex((v) => v.id === record.id)
    itemList.value.splice(index, 1)
    emit('del', index)
  }
  // 选择主要属性
  const changeMainAttr = (e, record) => {
    const checked = e.target.checked
    if (checked) {
      mainId.value = record.id
    } else {
      mainId.value = 0
    }
  }
  const handleAttrValue = (value) => {
    return value.map((v) => v.name).join('，')
  }
</script>

<style lang="scss" scoped>
  .attribute-table {
    margin-bottom: 15px;
  }
  .attr-value-list {
    max-height: 400px;
    overflow-y: auto;
    overflow-x: hidden;
  }
  .attr-value {
    margin: 6px 0;
    display: flex;
    align-items: center;
    .attr-value-input {
      width: 195px;
    }
    .attr-value-btn {
      margin-left: 6px;
      cursor: pointer;
      &.add {
        color: $ehf-color-blue;
      }
      &.remove {
        color: red;
      }
      &.hidden {
        visibility: hidden;
      }
    }
  }
  .ant-btn-link {
    padding: 0;
    & + & {
      margin-left: 10px;
    }
  }
  .sku-table-css {
    .table-footer {
      display: flex;
    }
    .add-btn {
      flex: 1;
      padding: 8px;
      text-align: center;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      & + .add-btn {
        border-left: 1px solid #e0e0e0;
      }
    }
  }
</style>
